// External Imports
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { getFormValues, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import _get from 'lodash/get';

// PPUI Imports
import { Container } from '@paypalcorp/pp-react-grid';
import { Divider } from '@paypalcorp/pp-react-divider';

// Internal Imports
import { BTN_FACTORY_V3_FORM } from '../../clientConstants';
import { validate } from '../../lib/clientValidatorV3';
import ButtonStyles from './ButtonStyles/ButtonStyles';
import StepHeader from './components/StepHeader';
import OrganizationName from './components/OrganizationName';
import Purpose from './components/Purpose';
import LogoExperience from '../logoExperience/LogoExperience';
import BannerImage from '../BannerImage/BannerImage';
import {
  isDonateButton,
  getOptionsForCountriesField,
  getOptionsForLanguageField,
  isHostedFlow,
} from '../../utils/buttonFactoryUtils';
import WizardNavButtons from '../navigationButtons/WizardNavButtons';
import { trackEventWithData } from '../../utilsV3/analytics';
import { FPTI_SPEC } from '../../utilsV3/fptiConstants';

const WizardStep1 = ({
  handleBack,
  handleSubmit,
  formValues,
  uploadImage,
  updateBusinessProfileLogo,
  updateBusinessImageUrls,
  application,
  dispatch,
  optionsForCountry,
  optionsForLanguage,
  setImageUpdated,
  dropzoneError,
  setDropzoneError,
  logoClientError,
  bannerClientError,
  showSpinner,
}) => {
  const trackPageLoadImpression = () => {
    if (isDonateButton()) {
      trackEventWithData(FPTI_SPEC.CONFIGURE_BUTTON_PAGE_IM);
    } else {
      trackEventWithData(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_IM, { step: 1 });
    }
  };

  useEffect(() => {
    trackPageLoadImpression();
  }, []);

  // On this step, formValues is undefined on the first render
  // Might be related to https://github.com/redux-form/redux-form/issues/3570
  if (!formValues) {
    return null;
  }

  return (
    <>
      <Container className="wizard-step-container wizard-step-1-container">
        <StepHeader />

        <form onSubmit={handleSubmit} className="wizard-form">
          {isDonateButton() ? (
            <ButtonStyles
              formValues={formValues}
              dispatch={dispatch}
              setImageUpdated={setImageUpdated}
              optionsForCountry={optionsForCountry}
              optionsForLanguage={optionsForLanguage}
              dropzoneError={dropzoneError}
              setDropzoneError={setDropzoneError}
              showBadge={application.showNewFeatureBadge ?? false}
            />
          ) : (
            <>
              <OrganizationName />
              {isHostedFlow() && (
                <>
                  <LogoExperience
                    isChecked={formValues.noLogoImage === 'no'}
                    uploadImage={uploadImage}
                    updateBusinessProfileLogo={updateBusinessProfileLogo}
                    updateBusinessImageUrls={updateBusinessImageUrls}
                    logoUrl={formValues.logoUrl}
                    logoTarget={formValues.logoTarget}
                    businessProfileLogoUrl={formValues.businessProfileLogoUrl}
                    logoClientError={logoClientError}
                    dispatch={dispatch}
                    noLogoImage={formValues.noLogoImage}
                  />
                  <BannerImage
                    isChecked={formValues.noBannerImage === 'no'}
                    uploadImage={uploadImage}
                    bannerImageUrl={formValues.bannerImageUrl}
                    bannerClientError={bannerClientError}
                    dispatch={dispatch}
                    showBadge={application.showNewFeatureBadge ?? false}
                  />
                </>
              )}
              <Divider />
              <Purpose />
            </>
          )}
        </form>
      </Container>
      <WizardNavButtons
        showSpinner={showSpinner}
        handleBack={handleBack}
        handleSubmit={handleSubmit}
      />
    </>
  );
};

WizardStep1.propTypes = {
  buttonFactoryV3: PropTypes.object,
  handleBack: PropTypes.func,
  handleSubmit: PropTypes.func,
  formValues: PropTypes.object,
  updateBusinessProfileLogo: PropTypes.func,
  updateBusinessImageUrls: PropTypes.func,
  uploadImage: PropTypes.func,
  setShowSpinner: PropTypes.bool,
  application: PropTypes.object,
  dispatch: PropTypes.func,
  onSubmit: PropTypes.func,
  setImageUpdated: PropTypes.func,
  dropzoneError: PropTypes.string,
  setDropzoneError: PropTypes.func,
  logoClientError: PropTypes.string,
  bannerClientError: PropTypes.string,
  showSpinner: PropTypes.bool,
};

let WizardStep1Form = reduxForm({
  form: BTN_FACTORY_V3_FORM,
  keepDirtyOnReinitialize: true,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate,
})(WizardStep1);

const mapStateToProps = (state) => {
  const formValues = getFormValues(BTN_FACTORY_V3_FORM)(state);
  const { application = {}, buttonFactoryV3 = {} } = state;
  const countryAndLanguageDetails = _get(
    application,
    'countryAndLanguageDetails'
  );
  const countryCode =
    _get(formValues, 'countryCode') ||
    _get(buttonFactoryV3, 'formattedButton.countryCode');

  return {
    formValues,
    application,
    initialValues: buttonFactoryV3.formattedButton,
    optionsForCountry: getOptionsForCountriesField(countryAndLanguageDetails),
    optionsForLanguage: getOptionsForLanguageField(
      countryAndLanguageDetails,
      countryCode
    ),
  };
};

const mapDispatchToProps = (dispatch) => {
  return { dispatch };
};

WizardStep1Form = connect(mapStateToProps, mapDispatchToProps)(WizardStep1Form);

export default WizardStep1Form;
